import { useNavigate } from 'react-router-dom';
import { ArrowRight, Circle, CheckCircle2, ListTodo, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCompletarTarea, usePermisosTareas, useResumenMias, useTareasLista } from '@/hooks/useTareas';
import { VencimientoTexto } from './TareaPiezas';

const PROXIMAS_MOSTRADAS = 5;

function Cifra({ valor, etiqueta, acento, onClick }: { valor: number; etiqueta: string; acento: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="flex-1 rounded-lg bg-gray-50 hover:bg-gray-100 px-3 py-2 text-left transition-colors">
      <span className={cn('block text-xl font-bold leading-tight', valor > 0 ? acento : 'text-text-primary')}>{valor}</span>
      <span className="block text-[11px] text-text-secondary">{etiqueta}</span>
    </button>
  );
}

/** Resumen de "Mis tareas" para el Dashboard: vencidas, de hoy, próximos 7 días y las 5 más próximas. */
export function MisTareasWidget() {
  const navigate = useNavigate();
  const { puedeUsar } = usePermisosTareas();
  const { data: resumen } = useResumenMias(puedeUsar);
  const { data, isLoading } = useTareasLista({ mias: true, completadas: 'no', limit: PROXIMAS_MOSTRADAS }, puedeUsar);
  const completar = useCompletarTarea();

  if (!puedeUsar) return null;

  const irAMias = () => navigate('/tareas?vista=mias');

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-text-secondary/50">Pendientes</p>
          <h3 className="text-sm font-bold text-text-primary mt-0.5 flex items-center gap-1.5">
            <ListTodo size={14} className="text-primary-500" /> Mis tareas
          </h3>
        </div>
        <button onClick={irAMias} className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1">
          Ver todas <ArrowRight size={12} />
        </button>
      </div>

      <div className="flex gap-2">
        <Cifra valor={resumen?.vencidas ?? 0} etiqueta="Vencidas" acento="text-red-600" onClick={irAMias} />
        <Cifra valor={resumen?.hoy ?? 0} etiqueta="Hoy" acento="text-amber-600" onClick={irAMias} />
        <Cifra valor={resumen?.proximos ?? 0} etiqueta="Próx. 7 días" acento="text-primary-600" onClick={irAMias} />
      </div>

      <div className="mt-3">
        {isLoading && <div className="py-4 flex justify-center text-gray-300"><Loader2 size={16} className="animate-spin" /></div>}
        {data && data.items.length === 0 && (
          <p className="py-4 text-center text-xs text-text-secondary">No tienes tareas pendientes.</p>
        )}
        <ul className="divide-y divide-gray-50">
          {data?.items.map(t => (
            <li
              key={t.id}
              onClick={() => navigate(`/tareas?vista=mias&t=${t.id}`)}
              className="flex items-center gap-2 py-2 cursor-pointer hover:bg-gray-50/60 -mx-2 px-2 rounded-lg"
            >
              <button
                onClick={e => { e.stopPropagation(); completar.mutate({ id: t.id, completar: true }); }}
                className="text-gray-300 hover:text-emerald-500 flex-shrink-0"
                aria-label="Completar tarea"
              >
                {completar.isPending && completar.variables?.id === t.id
                  ? <CheckCircle2 size={17} className="text-emerald-500" />
                  : <Circle size={17} />}
              </button>
              <span className="flex-1 min-w-0 text-sm text-text-primary truncate">{t.titulo}</span>
              <VencimientoTexto tarea={t} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
